// src/components/header.jsx
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Link, NavLink, useNavigate, useLocation, useParams } from "react-router-dom";
import api, { setApiLang } from "../lib/api";
import { withLangPrefix, stripLangPrefix, SUPPORTED_LANGS } from "../lib/i18nRoutes";
import { useTranslation } from "../i18n";

// ── Poll intervals ─────────────────────────────────────────────────────────────
const WEATHER_POLL_MS    = 15 * 60 * 1000;
const TRENDING_POLL_MS   =  5 * 60 * 1000;
const NAV_POLL_MS        = 10 * 60 * 1000;
const SEARCH_DEBOUNCE_MS = 360;
const SEARCH_LIMIT       = 8;

const FALLBACK_LOC = { lat: 33.5731, lon: -7.5898, city: "Casablanca", cc: "MA", tz: "Africa/Casablanca" };

const langs = [
  { code: "en", label: "EN", full: "English"  },
  { code: "fr", label: "FR", full: "Français" },
  { code: "ar", label: "ع",  full: "العربية"  },
  { code: "de", label: "DE", full: "Deutsch"  },
];

const SAFE_LOCALES     = ["en", "fr", "ar", "de"];
const STORAGE_LANG_KEY = "site_lang";

// ── Utilities ──────────────────────────────────────────────────────────────────
const formatDateLong = (d = new Date(), lang = "en") => {
  const safe = SAFE_LOCALES.includes(lang) ? lang : "en";
  return d.toLocaleDateString(safe, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
};

const weatherCodeToIcon = (code) => {
  if (code === 0)                                      return "bi-sun";
  if (code === 1 || code === 2)                        return "bi-brightness-high";
  if (code === 3)                                      return "bi-cloud";
  if (code === 45 || code === 48)                      return "bi-cloud-fog2";
  if (code >= 51 && code <= 57)                        return "bi-cloud-drizzle";
  if (code >= 61 && code <= 67)                        return "bi-cloud-rain";
  if (code >= 71 && code <= 77)                        return "bi-snow";
  if (code >= 80 && code <= 82)                        return "bi-cloud-rain-heavy";
  if (code === 85 || code === 86)                      return "bi-snow2";
  if (code === 95 || code === 96 || code === 99)       return "bi-cloud-lightning";
  return "bi-thermometer-sun";
};

const prettifySlug = (s) =>
  String(s || "").replace(/[-_]+/g, " ").replace(/^\w/, (c) => c.toUpperCase());

function parseJwt(token) {
  try {
    const base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(decodeURIComponent(
      Array.prototype.map.call(atob(base64), (c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2)).join("")
    ));
  } catch { return null; }
}

function chooseField(item, field, lang) {
  if (!item) return null;
  if (Array.isArray(item.translations)) {
    const tr = item.translations.find((t) => String(t.target_lang || "").startsWith(lang));
    if (tr?.[field]) return tr[field];
  }
  if (item.translations?.[lang]?.[field]) return item.translations[lang][field];
  if (item[`${field}_${lang}`])           return item[`${field}_${lang}`];
  if (item.i18n?.[lang]?.[field])         return item.i18n[lang][field];
  return item[field] ?? null;
}

function highlight(text = "", query = "") {
  if (!text || !query.trim()) return text;
  try {
    const esc = String(query).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const re  = new RegExp(`(${esc})`, "ig");
    return String(text).split(re).map((part, i) =>
      re.test(part)
        ? <mark key={i} style={{ background: "#fde047", padding: "0 1px", borderRadius: 2, color: "#111" }}>{part}</mark>
        : part
    );
  } catch { return text; }
}

// ═══════════════════════════════════════════════════════════════════════════════
export default function Header() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const params    = useParams();
  const { lang, t, setLang } = useTranslation();
  const isRTL = lang === "ar";

  // ── Refs ───────────────────────────────────────────────────────────────────
  const lastSyncedUrlLangRef = useRef(null);
  const lastTokenRef         = useRef(null);
  const confirmTimeoutRef    = useRef(null);
  const langChangeTimerRef   = useRef(null);
  const searchWrapDesktopRef = useRef(null);
  const searchInputRef       = useRef(null);
  const searchAbortRef       = useRef(null);
  const drawerRef            = useRef(null);

  // ── State ──────────────────────────────────────────────────────────────────
  const [isLoggedIn,     setIsLoggedIn]     = useState(false);
  const [userInfo,       setUserInfo]       = useState(null);
  const [confirmSignOut, setConfirmSignOut] = useState(false);

  const [query,          setQuery]          = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [searchResults,  setSearchResults]  = useState([]);
  const [searchLoading,  setSearchLoading]  = useState(false);
  const [searchOpen,     setSearchOpen]     = useState(false);
  const [focusedIdx,     setFocusedIdx]     = useState(-1);

  const [trending, setTrending] = useState([]);
  const [tIdx,     setTIdx]     = useState(0);

  const [weather, setWeather] = useState({
    ready: false,
    city:  FALLBACK_LOC.city,
    tempC: null,
    icon:  "bi-thermometer-sun",
  });

  const [navCats,          setNavCats]          = useState([]);
  const [subjects,         setSubjects]         = useState([]);
  const [catDetail,        setCatDetail]        = useState(null);
  const [catDetailLoading, setCatDetailLoading] = useState(false);

  const [drawerOpen,   setDrawerOpen]   = useState(false);
  const [expandedCats, setExpandedCats] = useState({});
  const [activeSearch, setActiveSearch] = useState(false);

  const today   = useMemo(() => formatDateLong(new Date(), lang), [lang]);
  const isAbort = (err) => err?.name === "AbortError" || String(err?.message || "").toLowerCase().includes("aborted");

  // ── URL lang sync ──────────────────────────────────────────────────────────
  useEffect(() => {
    const p = params?.lang || null;
    if (!p || p === lastSyncedUrlLangRef.current || !SUPPORTED_LANGS.includes(p)) return;
    lastSyncedUrlLangRef.current = p;
    try { setLang(p); } catch {}
    try { setApiLang(p); } catch {}
    try { localStorage.setItem(STORAGE_LANG_KEY, p); } catch {}
  }, [params?.lang]);

  useEffect(() => { try { setApiLang(lang); } catch {} }, [lang]);

  // ── Body scroll lock ───────────────────────────────────────────────────────
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [drawerOpen]);

  // ── Drawer outside-click close ─────────────────────────────────────────────
  useEffect(() => {
    if (!drawerOpen) return;
    const h = (e) => {
      if (drawerRef.current && !drawerRef.current.contains(e.target)) setDrawerOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [drawerOpen]);

  // ── Auth ───────────────────────────────────────────────────────────────────
  const checkLoginState = useCallback(() => {
    const token = localStorage.getItem("accessToken") || sessionStorage.getItem("accessToken");
    const p = token ? parseJwt(token) : null;
    // A JWT with an `exp` claim in the past is a stale/expired token. Treat
    // it exactly like "logged out" instead of attaching it to every request
    // — otherwise this global default header poisons PUBLIC endpoints too
    // (e.g. the home page's article list), turning a harmless expired
    // session into a hard 401 for every visitor, logged in or not.
    const isExpired = p?.exp && Date.now() >= p.exp * 1000;

    if (token && !isExpired) {
      setIsLoggedIn(true);
      setUserInfo(p ? { username: p.username || p.user || p.sub || p.email || null } : null);
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      if (isExpired) {
        try { localStorage.removeItem("accessToken"); sessionStorage.removeItem("accessToken"); } catch {}
      }
      setIsLoggedIn(false); setUserInfo(null);
      delete api.defaults.headers.common["Authorization"];
    }
    lastTokenRef.current = (token && !isExpired) ? token : null;
  }, []);

  useEffect(() => {
    checkLoginState();
    const onStorage = (e) => {
      if (["accessToken", "refreshToken", "___auth_update"].includes(e.key)) checkLoginState();
    };
    window.addEventListener("storage",      onStorage);
    window.addEventListener("auth:login",   checkLoginState);
    window.addEventListener("auth:logout",  checkLoginState);
    window.addEventListener("auth:changed", checkLoginState);
    return () => {
      window.removeEventListener("storage",      onStorage);
      window.removeEventListener("auth:login",   checkLoginState);
      window.removeEventListener("auth:logout",  checkLoginState);
      window.removeEventListener("auth:changed", checkLoginState);
    };
  }, [checkLoginState]);

  useEffect(() => {
    const readToken = () => localStorage.getItem("accessToken") || sessionStorage.getItem("accessToken") || null;
    const onFocusOrVisible = () => { if (readToken() !== lastTokenRef.current) checkLoginState(); };
    window.addEventListener("focus", onFocusOrVisible);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") onFocusOrVisible();
    });
    const iv = setInterval(() => { if (readToken() !== lastTokenRef.current) checkLoginState(); }, 5000);
    return () => { clearInterval(iv); window.removeEventListener("focus", onFocusOrVisible); };
  }, [checkLoginState]);

  useEffect(() => () => { if (confirmTimeoutRef.current) clearTimeout(confirmTimeoutRef.current); }, []);

  const handleLogout = useCallback((e) => {
    e?.preventDefault();
    try { localStorage.removeItem("accessToken"); localStorage.removeItem("refreshToken"); } catch {}
    try { sessionStorage.removeItem("accessToken"); sessionStorage.removeItem("refreshToken"); } catch {}
    delete api.defaults.headers.common["Authorization"];
    setIsLoggedIn(false); setUserInfo(null); setConfirmSignOut(false); setDrawerOpen(false);
    if (confirmTimeoutRef.current) { clearTimeout(confirmTimeoutRef.current); confirmTimeoutRef.current = null; }
    try { localStorage.setItem("___auth_update", Date.now().toString()); localStorage.removeItem("___auth_update"); } catch {}
    try { window.dispatchEvent(new Event("auth:logout")); } catch {}
    navigate(withLangPrefix("/", lang), { replace: true });
  }, [navigate, lang]);

  // ── Language change: STAY ON CURRENT PAGE ────────────────────────────────
  // ★ FIX: instead of always going to "/", we strip the current lang prefix
  //   and rebuild the same path with the new language.
  //   e.g. user on /fr/profile → changes to ar → goes to /ar/profile ✅
  const onChangeLang = useCallback((newLang) => {
    if (!newLang || newLang === lang) return;
    if (langChangeTimerRef.current) clearTimeout(langChangeTimerRef.current);

    // Close all panels immediately
    setDrawerOpen(false);
    setActiveSearch(false);

    langChangeTimerRef.current = setTimeout(() => {
      langChangeTimerRef.current = null;

      try { setLang(newLang); } catch {}
      try { setApiLang(newLang); } catch {}
      try { localStorage.setItem(STORAGE_LANG_KEY, newLang); } catch {}
      try {
        window.dispatchEvent(new CustomEvent("site:language_changed", { detail: { lang: newLang } }));
      } catch {}

      // ★ KEY FIX: keep the user on the same page — only swap the lang prefix
      const { rest } = stripLangPrefix(location.pathname || "/");
      // rest is something like "/profile", "/subject/tech", etc.
      const samePage = withLangPrefix(rest || "/", newLang);
      navigate(samePage, { replace: true });
    }, 120);
  }, [lang, location.pathname, navigate, setLang]);

  // Sync lang change from another tab
  useEffect(() => {
    const onStorageLang = (e) => {
      if (!e || e.key !== STORAGE_LANG_KEY) return;
      const nl = e.newValue;
      if (!nl || !SUPPORTED_LANGS.includes(nl) || nl === lang) return;
      try { setLang(nl); } catch {}
      try { setApiLang(nl); } catch {}
      // For cross-tab sync: rebuild current path with new lang
      const { rest } = stripLangPrefix(location.pathname || "/");
      try { window.location.replace(withLangPrefix(rest || "/", nl)); } catch {}
    };
    window.addEventListener("storage", onStorageLang);
    return () => window.removeEventListener("storage", onStorageLang);
  }, [lang, location.pathname, setLang]);

  // ── Search debounce ────────────────────────────────────────────────────────
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  // ── Live search ────────────────────────────────────────────────────────────
  useEffect(() => {
    const q      = debouncedQuery.trim();
    const minLen = isRTL ? 1 : 2;
    if (q.length < minLen) { setSearchResults([]); setSearchOpen(false); setFocusedIdx(-1); return; }
    if (searchAbortRef.current) searchAbortRef.current.abort();
    const ctrl = new AbortController();
    searchAbortRef.current = ctrl;
    setSearchLoading(true);
    api.get("/articles/", {
      params:  { search: q, page_size: SEARCH_LIMIT, ordering: "-published_at", lang },
      signal:  ctrl.signal,
      headers: { "Accept-Language": lang, "Content-Type": "application/json" },
    })
      .then((res) => {
        const raw   = res.data?.results ?? res.data ?? [];
        const items = Array.isArray(raw) ? raw.slice(0, SEARCH_LIMIT) : [];
        setSearchResults(items);
        setSearchOpen(items.length > 0);
        setFocusedIdx(-1);
      })
      .catch((err) => { if (!isAbort(err)) { setSearchResults([]); setSearchOpen(false); } })
      .finally(() => setSearchLoading(false));
    return () => ctrl.abort();
  }, [debouncedQuery, lang, isRTL]);

  // ── Click outside desktop search close ────────────────────────────────────
  useEffect(() => {
    const h = (e) => {
      if (searchWrapDesktopRef.current && !searchWrapDesktopRef.current.contains(e.target)) {
        setSearchOpen(false); setFocusedIdx(-1);
      }
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  // ── Keyboard ───────────────────────────────────────────────────────────────
  const onSearchKeyDown = (e) => {
    if (!searchOpen) return;
    if      (e.key === "ArrowDown") { e.preventDefault(); setFocusedIdx((i) => Math.min(i + 1, searchResults.length - 1)); }
    else if (e.key === "ArrowUp")   { e.preventDefault(); setFocusedIdx((i) => Math.max(i - 1, -1)); }
    else if (e.key === "Escape")    { setSearchOpen(false); setFocusedIdx(-1); searchInputRef.current?.blur(); setActiveSearch(false); }
    else if (e.key === "Enter") {
      e.preventDefault();
      const hit = focusedIdx >= 0 ? searchResults[focusedIdx] : searchResults[0];
      if (hit) goToArticle(hit);
    }
  };

  const goToArticle = (item) => {
    clearSearch(); setDrawerOpen(false); setActiveSearch(false);
    navigate(withLangPrefix(`/articles/${encodeURIComponent(item.slug || item.id)}`, lang));
  };

  const clearSearch = () => { setQuery(""); setDebouncedQuery(""); setSearchResults([]); setSearchOpen(false); setFocusedIdx(-1); };

  const onSubmitSearch = (e) => {
    e?.preventDefault();
    if (!query.trim()) return;
    const hit = focusedIdx >= 0 ? searchResults[focusedIdx] : searchResults[0];
    if (hit) goToArticle(hit);
  };

  // ── Trending ───────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const ctrl    = new AbortController();
    const load    = async () => {
      try {
        const res    = await api.get("/articles/trending/", { params: { limit: 10 }, signal: ctrl.signal });
        if (cancelled) return;
        const items  = Array.isArray(res.data) ? res.data : res.data.results || [];
        const mapped = items.map((a) => {
          const slug     = a.slug ?? "";
          const hrefPath = typeof a.url === "string" && a.url.startsWith("/articles/")
            ? a.url
            : `/articles/${encodeURIComponent(slug)}`;
          return { id: a.id, title: a.title, href: withLangPrefix(hrefPath, lang), image_url: a.image_url ?? null };
        });
        setTrending(mapped); setTIdx(0);
      } catch (err) {
        if (!isAbort(err) && !ctrl.signal.aborted) setTrending([{ title: t("loading"), href: "#" }]);
      }
    };
    load();
    const iv = setInterval(load, TRENDING_POLL_MS);
    return () => { cancelled = true; ctrl.abort(); clearInterval(iv); };
  }, [lang, t]);

  useEffect(() => {
    if (!trending.length) return;
    const iv = setInterval(() => setTIdx((i) => (i + 1) % trending.length), 6000);
    return () => clearInterval(iv);
  }, [trending.length]);

  // ── Nav ────────────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const ctrl    = new AbortController();
    const buildTree = (flat) => {
      const map = {}; flat.forEach((c) => (map[c.id] = { ...c, children: [] }));
      const roots = [];
      flat.forEach((c) => {
        const pf  = c.parent;
        const pid = pf == null ? null : typeof pf === "number" ? pf : pf?.id ?? null;
        if (pid && map[pid]) map[pid].children.push(map[c.id]); else roots.push(map[c.id]);
      });
      return roots;
    };
    const fetchCats = async () => {
      try {
        const r = await api.get("/categories/tree/", { signal: ctrl.signal });
        const d = r?.data ?? [];
        if (Array.isArray(d) && d.length > 0 && (d[0].children !== undefined || d[0].parent !== undefined)) {
          if (!cancelled) setNavCats(d); return;
        }
        const r2   = await api.get("/categories/", { signal: ctrl.signal, params: { page_size: 200 } });
        const flat = Array.isArray(r2.data) ? r2.data : r2.data.results || [];
        if (!cancelled) setNavCats(buildTree(flat));
      } catch (e) { if (!cancelled && !isAbort(e)) setNavCats([]); }
    };
    const fetchSubjects = async () => {
      try {
        const r     = await api.get("/subjects/", { signal: ctrl.signal, params: { page_size: 200, lang } });
        const data  = r?.data ?? [];
        const items = Array.isArray(data) ? data : data.results || [];
        if (!cancelled) setSubjects(items.slice(0, 24));
      } catch (e) { if (!cancelled && !isAbort(e)) setSubjects([]); }
    };
    fetchCats(); fetchSubjects();
    const iv = setInterval(() => { fetchCats(); fetchSubjects(); }, NAV_POLL_MS);
    return () => { cancelled = true; ctrl.abort(); clearInterval(iv); };
  }, [lang]);

 // ── Weather ────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const abort = new AbortController();

    const fetchWeather = async ({ lat, lon, city }) => {
      const vars = "temperature_2m,weather_code,is_day,wind_speed_10m";
      const r    = await fetch(
        `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=${vars}&timezone=auto&temperature_unit=celsius`,
        { signal: abort.signal }
      );
      const d = await r.json();
      const c = d.current || {};
      setWeather({
        ready: true,
        city,
        tempC: typeof c.temperature_2m === "number" ? Math.round(c.temperature_2m) : null,
        icon:  weatherCodeToIcon(c.weather_code),
      });
    };

    const reverseGeocode = async (lat, lon) => {
      try {
        const r = await fetch(
          `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json&zoom=10&addressdetails=1`,
          { signal: abort.signal, headers: { "Accept-Language": "en", "User-Agent": "24News/1.0" } }
        );
        const j    = await r.json();
        const addr = j.address || {};
        const city = addr.city || addr.town || addr.village || addr.county || addr.state || addr.country || "Unknown";
        const cc   = addr.country_code ? addr.country_code.toUpperCase() : "";
        return cc ? `${city}, ${cc}` : city;
      } catch { return null; }
    };

    const getIPLocation = async () => {
      try {
        const r = await fetch(
          "https://ipwho.is/?fields=success,city,country_code,latitude,longitude,timezone.id",
          { signal: abort.signal }
        );
        const j = await r.json();
        if (!j.success) return FALLBACK_LOC;
        const lat = Number(j.latitude), lon = Number(j.longitude);
        if (!isFinite(lat) || !isFinite(lon)) return FALLBACK_LOC;
        const city = [j.city, j.country_code].filter(Boolean).join(", ") || FALLBACK_LOC.city;
        return { lat, lon, city };
      } catch { return FALLBACK_LOC; }
    };

const ipFallback = async () => {
      try {
        const loc = await getIPLocation();
        await fetchWeather(loc);
      } catch {
        setWeather({ ready: true, city: `${FALLBACK_LOC.city}, ${FALLBACK_LOC.cc}`, tempC: null, icon: "bi-brightness-high" });
      }
    };

    const init = () => {
      if ("geolocation" in navigator) {
        navigator.geolocation.getCurrentPosition(
          async (pos) => {
            try {
              const lat  = pos.coords.latitude;
              const lon  = pos.coords.longitude;
              const city = await reverseGeocode(lat, lon) || FALLBACK_LOC.city;
              await fetchWeather({ lat, lon, city });
            } catch { ipFallback(); }
          },
          () => ipFallback(),
          { timeout: 8000, maximumAge: 5 * 60 * 1000, enableHighAccuracy: false }
        );
      } else {
        ipFallback();
      }
    };

    init();
    const iv = setInterval(init, WEATHER_POLL_MS);
    return () => { abort.abort(); clearInterval(iv); };
  }, []);

  // ── Category breadcrumb ────────────────────────────────────────────────────
  const categorySlugFromPath = useMemo(() => {
    const { rest } = stripLangPrefix(location.pathname || "/");
    const m = rest.match(/^\/?category\/([^/]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  }, [location.pathname]);

  useEffect(() => {
    if (!categorySlugFromPath) { setCatDetail(null); setCatDetailLoading(false); return; }
    const found = navCats.some(
      (c) => c.slug === categorySlugFromPath || c.children?.some((x) => x.slug === categorySlugFromPath)
    );
    if (found) { setCatDetail(null); return; }
    let cancelled = false;
    const ctrl    = new AbortController();
    setCatDetailLoading(true);
    api.get(`/categories/${encodeURIComponent(categorySlugFromPath)}/`, { signal: ctrl.signal })
      .then((r) => { if (!cancelled) setCatDetail(r.data); })
      .catch((e) => { if (!cancelled && !isAbort(e)) setCatDetail(null); })
      .finally(() => { if (!cancelled) setCatDetailLoading(false); });
    return () => { cancelled = true; ctrl.abort(); };
  }, [categorySlugFromPath, navCats, lang]);

  const categoryTitle = useMemo(() => {
    const slug = categorySlugFromPath;
    if (!slug) return null;
    for (const c of navCats) {
      if (c.slug === slug) return c.name;
      const ch = c.children?.find((x) => x.slug === slug);
      if (ch) return `${c.name} › ${ch.name}`;
    }
    return catDetail?.name || catDetail?.title || prettifySlug(slug);
  }, [categorySlugFromPath, navCats, catDetail]);

  // ── Helpers ────────────────────────────────────────────────────────────────
  const link = (path) => withLangPrefix(path, lang);

  const initials = (() => {
    const parts = String(userInfo?.username || "").split(/[\s._-]+/).filter(Boolean);
    return parts.length ? (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase() : "U";
  })();

  const currentItem = trending[tIdx] || {};
  const toggleCatExpand = (id) => setExpandedCats((p) => ({ ...p, [id]: !p[id] }));

  const requestSignOut = (e) => {
    e?.preventDefault(); e?.stopPropagation();
    if (!confirmSignOut) {
      setConfirmSignOut(true);
      if (confirmTimeoutRef.current) clearTimeout(confirmTimeoutRef.current);
      confirmTimeoutRef.current = setTimeout(() => {
        setConfirmSignOut(false); confirmTimeoutRef.current = null;
      }, 5000);
      return;
    }
    handleLogout(e);
  };

  const cancelSignOut = (e) => {
    e?.preventDefault(); e?.stopPropagation();
    if (confirmTimeoutRef.current) { clearTimeout(confirmTimeoutRef.current); confirmTimeoutRef.current = null; }
    setConfirmSignOut(false);
  };

  // ── Search dropdown ────────────────────────────────────────────────────────
  const searchDropdown = searchOpen ? (
    <div
      className={`hs-dropdown${isRTL ? " hs-dropdown--rtl" : ""}`}
      role="listbox"
      aria-label="Search results"
      dir={isRTL ? "rtl" : "ltr"}
    >
      {searchResults.length === 0 ? (
        <div className="hs-empty">
          <i className="bi bi-search hs-empty-icon" />
          {t("no_results") || (isRTL ? "لا توجد نتائج" : "No results")}
        </div>
      ) : (
        <>
          {searchResults.map((item, i) => {
            const title   = chooseField(item, "title",   lang) || item.title   || "Untitled";
            const excerpt = chooseField(item, "excerpt", lang) || item.excerpt || "";
            const catName = item.categories?.[0]?.name || item.section || item.category_name || "";
            const thumb   = item.image_url || item.image || item.thumbnail || null;
            const focused = i === focusedIdx;
            return (
              <button
                key={item.id ?? item.slug ?? i}
                type="button" role="option" aria-selected={focused}
                className={`hs-result${focused ? " hs-result--focused" : ""}${isRTL ? " hs-result--rtl" : ""}`}
                onClick={() => goToArticle(item)}
                onMouseEnter={() => setFocusedIdx(i)}
              >
                <div className="hs-thumb">
                  {thumb
                    ? <img src={thumb} alt="" loading="lazy"
                        onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "flex"; }}
                      />
                    : null}
                  <div className="hs-thumb-fallback" style={{ display: thumb ? "none" : "flex" }}>
                    <i className="bi bi-newspaper" />
                  </div>
                </div>
                <div className="hs-body" dir={isRTL ? "rtl" : "ltr"}>
                  {catName && <span className="hs-cat">{catName}</span>}
                  <div className="hs-title">{highlight(title, debouncedQuery)}</div>
                  {excerpt && <div className="hs-excerpt">{excerpt}</div>}
                </div>
                <i className={`bi ${isRTL ? "bi-arrow-left" : "bi-arrow-right"} hs-arrow`} aria-hidden />
              </button>
            );
          })}
          <div className="hs-footer" dir={isRTL ? "rtl" : "ltr"}>
            <span className="hs-footer-label">
              {searchResults.length} {t("results") || (isRTL ? "نتيجة" : "results")} · <em>"{debouncedQuery}"</em>
            </span>
            <kbd className="hs-kbd">↑↓ Enter</kbd>
          </div>
        </>
      )}
    </div>
  ) : null;

  // ── Search bar inner ───────────────────────────────────────────────────────
  const searchBarInner = (
    <div className={`hs-bar${isRTL ? " hs-bar--rtl" : ""}`}>
      <i className="bi bi-search hs-icon" aria-hidden />
      <input
        ref={searchInputRef}
        type="search"
        className="hs-input"
        dir={isRTL ? "rtl" : "ltr"}
        lang={lang}
        placeholder={t("search_placeholder") || (isRTL ? "ابحث…" : "Search articles…")}
        value={query}
        autoComplete="off"
        spellCheck={false}
        aria-label="Search"
        aria-autocomplete="list"
        aria-expanded={searchOpen}
        aria-haspopup="listbox"
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => { if (searchResults.length > 0) setSearchOpen(true); }}
        onKeyDown={onSearchKeyDown}
      />
      {searchLoading && <span className="hs-spinner" aria-hidden />}
      {query && !searchLoading && (
        <button type="button" className="hs-btn-icon" onClick={clearSearch} aria-label="Clear">
          <i className="bi bi-x-lg" />
        </button>
      )}
      <button type="submit" className="hs-btn-go" aria-label="Search">
        <i className={`bi ${isRTL ? "bi-arrow-left" : "bi-arrow-right"}`} />
      </button>
    </div>
  );

  // ── Desktop nav category renderer ─────────────────────────────────────────
  const renderDesktopCat = (cat) => {
    const hasKids  = Array.isArray(cat.children) && cat.children.length > 0;
    const baseLink = link(`/category/${cat.slug}`);
    if (!hasKids) {
      return (
        <li key={cat.id} className="dnav-item">
          <NavLink to={baseLink} className="dnav-link">{cat.name || cat.slug}</NavLink>
        </li>
      );
    }
    return (
      <li key={cat.id} className="dnav-item dnav-has-mega">
        <NavLink to={baseLink} className="dnav-link dnav-link--toggle" data-bs-toggle="dropdown" aria-expanded="false">
          {cat.name}
          <i className="bi bi-chevron-down dnav-chev" aria-hidden />
        </NavLink>
        <div className="dropdown-menu dnav-mega p-3">
          <div className="dnav-mega-grid">
            {cat.children.map((ch) => (
              <NavLink key={ch.id} to={link(`/category/${ch.slug}`)} className="dnav-mega-item">
                {ch.name}
              </NavLink>
            ))}
          </div>
          <div className="dnav-mega-footer">
            <NavLink to={baseLink} className="dnav-see-all">
              {t("see_all_in", { name: cat.name }) || `All in ${cat.name}`}
              <i className={`bi ${isRTL ? "bi-arrow-left" : "bi-arrow-right"} ms-1`} />
            </NavLink>
          </div>
        </div>
      </li>
    );
  };

  // ══════════════════════════════════════════════════════════════════════════
  return (
    <>
      {/* ═══════════ HEADER ═══════════ */}
      <header className={`nh${isRTL ? " nh--rtl" : ""}`} dir={isRTL ? "rtl" : "ltr"}>

        {/* TOP STRIP */}
        <div className="nh-strip">
          <div className="nh-container nh-strip-inner">
            <div className="nh-ticker-wrap">
              <span className="nh-badge">
                <i className="bi bi-fire" aria-hidden />
                <span className="nh-badge-txt">{t("trending") || "Trending"}</span>
              </span>
              <div className="nh-ticker-track">
                <Link to={currentItem.href || "#"} className="nh-ticker-text">
                  {currentItem.title || t("welcome_newsroom") || "Welcome"}
                </Link>
              </div>
              <div className="nh-ticker-btns">
                <button type="button" className="nh-tick-btn"
                  onClick={() => setTIdx((i) => (trending.length ? (i - 1 + trending.length) % trending.length : 0))}
                  aria-label="Previous">
                  <i className="bi bi-chevron-left" />
                </button>
                <button type="button" className="nh-tick-btn"
                  onClick={() => setTIdx((i) => (trending.length ? (i + 1) % trending.length : 0))}
                  aria-label="Next">
                  <i className="bi bi-chevron-right" />
                </button>
              </div>
            </div>

            <div className="nh-strip-meta">
              <span className="nh-date">{today}</span>

              {/* ★ FIX: weather city now always visible — no media-query hide */}
              {weather.tempC !== null && (
                <span className="nh-weather">
                  <i className={`bi ${weather.icon}`} />
                  {weather.tempC}°C
                  {weather.city && (
                    <span className="nh-weather-city">{weather.city}</span>
                  )}
                </span>
              )}

              <div className="nh-socials">
                {[
                  ["bi-twitter-x",  "https://x.com/0news_247969",  "Twitter"],
                  ["bi-facebook",   "https://www.facebook.com/profile.php?id=61579447607022", "Facebook"],
                  ["bi-instagram",  "https://www.instagram.com/24.0_news/","Instagram"],
                  ["bi-youtube",    "https://youtube.com/@news24-now?si=SRo_3hctE-kQBzZr",  "YouTube"],
                ].map(([ico, href, lbl]) => (
                  <a key={lbl} href={href} target="_blank" rel="noopener noreferrer" className="nh-social" aria-label={lbl}>
                    <i className={`bi ${ico}`} />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* MASTHEAD */}
        <div className="nh-masthead">
          <div className="nh-container nh-masthead-inner">

            {/* Logo */}
            <Link to={link("/")} className="nh-brand" aria-label="24 News home">
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none" className="nh-logo-svg" aria-hidden>
                <rect width="32" height="32" rx="7" fill="#e63311"/>
                <path d="M6 9h4.5l8 10.5V9h4.5v14h-4.5L10.5 12.5V23H6V9z" fill="white"/>
              </svg>
              <div className="nh-brand-text">
                <span className="nh-brand-name">
                  <span className="nh-brand-num">24</span>News
                </span>
                <span className="nh-brand-sub">{t("read_everything") || "Read everything"}</span>
              </div>
            </Link>

            {/* Desktop search */}
            <form
              ref={searchWrapDesktopRef}
              className="hs-wrap hs-wrap--desktop"
              onSubmit={onSubmitSearch}
              role="search"
              aria-label="Search articles"
            >
              {searchBarInner}
              {searchDropdown}
            </form>

            {/* Right actions */}
            <div className="nh-actions">

              {/* Language pills — desktop */}
              <div className="nh-lang-grp" role="group" aria-label="Language">
                {langs.map((l) => (
                  <button
                    key={l.code}
                    type="button"
                    title={l.full}
                    className={`nh-lang-btn${lang === l.code ? " nh-lang-btn--on" : ""}`}
                    onClick={() => onChangeLang(l.code)}
                    aria-pressed={lang === l.code}
                  >
                    {l.label}
                  </button>
                ))}
              </div>

              {/* Auth — desktop */}
              {isLoggedIn ? (
                <div className="dropdown nh-user-dd">
                  <button type="button" className="nh-avatar-btn" data-bs-toggle="dropdown" aria-expanded="false" aria-label="Account">
                    <span className="nh-avatar" aria-hidden>{initials}</span>
                    <span className="nh-avatar-name">{userInfo?.username || t("profile")}</span>
                    <i className="bi bi-chevron-down nh-avatar-chev" />
                  </button>
                  <ul className="dropdown-menu dropdown-menu-end nh-user-menu">
                    <li>
                      <div className="nh-user-header">
                        <span className="nh-avatar nh-avatar--lg">{initials}</span>
                        <span className="nh-user-name">{userInfo?.username || t("profile")}</span>
                      </div>
                    </li>
                    <li><hr className="dropdown-divider my-1" /></li>
                    <li>
                      <NavLink className="dropdown-item nh-dditem" to={link("/profile")}>
                        <i className="bi bi-person" /> {t("profile")}
                      </NavLink>
                    </li>
                    <li><hr className="dropdown-divider my-1" /></li>
                    <li>
                      {confirmSignOut ? (
                        <div className="nh-confirm-row">
                          <span className="nh-confirm-q">{t("confirm_sign_out") || "Sure?"}</span>
                          <button type="button" className="nh-confirm-yes" onClick={requestSignOut}>{t("yes") || "Yes"}</button>
                          <button type="button" className="nh-confirm-no"  onClick={cancelSignOut}>{t("cancel") || "No"}</button>
                        </div>
                      ) : (
                        <button type="button" className="dropdown-item nh-dditem nh-dditem--danger" onClick={requestSignOut}>
                          <i className="bi bi-box-arrow-right" /> {t("sign_out")}
                        </button>
                      )}
                    </li>
                  </ul>
                </div>
              ) : (
                <div className="nh-auth-btns">
                  <Link to={link("/login")}    className="nh-btn-ghost">{t("sign_in")  || "Sign in"}</Link>
                  <Link to={link("/register")} className="nh-btn-solid">{t("register") || "Register"}</Link>
                </div>
              )}

              {/* Mobile: search icon */}
              <button
                type="button"
                className="nh-icon-btn nh-search-toggle"
                onClick={() => { setActiveSearch((s) => !s); if (drawerOpen) setDrawerOpen(false); }}
                aria-label={activeSearch ? "Close search" : "Search"}
                aria-expanded={activeSearch}
              >
                <i className={`bi ${activeSearch ? "bi-x-lg" : "bi-search"}`} />
              </button>

              {/* Mobile: hamburger */}
              <button
                type="button"
                className={`nh-hamburger${drawerOpen ? " nh-hamburger--open" : ""}`}
                onClick={() => { setDrawerOpen((o) => !o); if (activeSearch) setActiveSearch(false); }}
                aria-label={drawerOpen ? "Close menu" : "Open menu"}
                aria-expanded={drawerOpen}
              >
                <span /><span /><span />
              </button>
            </div>
          </div>

          {/* Mobile search slide-down */}
          <div className={`nh-mobile-search${activeSearch ? " nh-mobile-search--open" : ""}`}>
            <div className="nh-container">
              <form
                className="hs-wrap"
                style={{ width: "100%", position: "relative" }}
                onSubmit={(e) => { onSubmitSearch(e); setActiveSearch(false); }}
                role="search"
              >
                {searchBarInner}
                {searchDropdown}
              </form>
            </div>
          </div>
        </div>

        {/* STICKY DESKTOP NAV */}
        <nav className="nh-nav" aria-label="Main navigation">
          <div className="nh-container nh-nav-inner">
            <ul className="nh-nav-list" role="list">
              <li className="dnav-item">
                <NavLink end to={link("/")} className="dnav-link">{t("home") || "Home"}</NavLink>
              </li>
              {navCats.length > 0
                ? navCats.map(renderDesktopCat)
                : <li className="dnav-item"><span className="dnav-link" style={{ color: "#bbb", cursor: "default" }}>{t("loading") || "…"}</span></li>
              }
              <li className="dnav-item">
                <NavLink to={link("/subjects")} className="dnav-link">{t("subjects") || "Subjects"}</NavLink>
              </li>
              <li className="dnav-item">
                <NavLink to={link("/contact")} className="dnav-link">{t("contact") || "Contact"}</NavLink>
              </li>
            </ul>
            {categoryTitle && (
              <div className="nh-cat-crumb" title={categoryTitle}>
                <i className="bi bi-folder2" />
                <span>{categoryTitle}</span>
                {catDetailLoading && <span style={{ color: "#bbb" }}>…</span>}
              </div>
            )}
          </div>
        </nav>
      </header>

      {/* ═══════════ OVERLAY ═══════════ */}
      <div
        className={`nh-overlay${drawerOpen ? " nh-overlay--on" : ""}`}
        aria-hidden
        onClick={() => setDrawerOpen(false)}
      />

      {/* ═══════════ DRAWER ═══════════ */}
      <div
        ref={drawerRef}
        className={`nh-drawer${drawerOpen ? " nh-drawer--open" : ""}${isRTL ? " nh-drawer--rtl" : ""}`}
        aria-modal="true"
        role="dialog"
        aria-label="Navigation menu"
        dir={isRTL ? "rtl" : "ltr"}
      >
        {/* Drawer head */}
        <div className="nh-drawer-head">
          <Link to={link("/")} className="nh-brand nh-brand--sm" onClick={() => setDrawerOpen(false)}>
            <svg width="26" height="26" viewBox="0 0 32 32" fill="none" aria-hidden>
              <rect width="32" height="32" rx="7" fill="#e63311"/>
              <path d="M6 9h4.5l8 10.5V9h4.5v14h-4.5L10.5 12.5V23H6V9z" fill="white"/>
            </svg>
            <span className="nh-brand-name"><span className="nh-brand-num">24</span>News</span>
          </Link>
          <button type="button" className="nh-drawer-close" onClick={() => setDrawerOpen(false)} aria-label="Close menu">
            <i className="bi bi-x-lg" />
          </button>
        </div>

        {/* Drawer body */}
        <div className="nh-drawer-body">

          {/* Auth */}
          <div className="nh-dsec">
            {isLoggedIn ? (
              <div className="nh-drawer-user">
                <span className="nh-avatar nh-avatar--lg">{initials}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="nh-user-name">{userInfo?.username || t("profile")}</div>
                  <NavLink to={link("/profile")} className="nh-drawer-profile-link" onClick={() => setDrawerOpen(false)}>
                    {t("profile")} →
                  </NavLink>
                </div>
                {confirmSignOut ? (
                  <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                    <button type="button" className="nh-confirm-yes" onClick={handleLogout}>{t("yes") || "Yes"}</button>
                    <button type="button" className="nh-confirm-no"  onClick={cancelSignOut}>{t("cancel") || "No"}</button>
                  </div>
                ) : (
                  <button type="button" className="nh-icon-btn nh-logout-btn" onClick={requestSignOut} aria-label="Sign out">
                    <i className="bi bi-box-arrow-right" />
                  </button>
                )}
              </div>
            ) : (
              <div className="nh-drawer-auth">
                <Link to={link("/login")}    className="nh-btn-ghost nh-btn--full" onClick={() => setDrawerOpen(false)}>{t("sign_in")  || "Sign in"}</Link>
                <Link to={link("/register")} className="nh-btn-solid nh-btn--full" onClick={() => setDrawerOpen(false)}>{t("register") || "Register"}</Link>
              </div>
            )}
          </div>

          {/* Language */}
          <div className="nh-dsec">
            <div className="nh-dlabel">{t("language") || "Language"}</div>
            <div className="nh-lang-pills">
              {langs.map((l) => (
                <button
                  key={l.code}
                  type="button"
                  className={`nh-lang-pill${lang === l.code ? " nh-lang-pill--on" : ""}`}
                  onClick={() => onChangeLang(l.code)}
                  aria-pressed={lang === l.code}
                >
                  <span className="nh-pill-code">{l.label}</span>
                  <span className="nh-pill-name">{l.full}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Navigation */}
          <div className="nh-dsec">
            <div className="nh-dlabel">{t("navigation") || "Navigation"}</div>
            <nav aria-label="Mobile navigation">
              <NavLink end to={link("/")} className="nh-dlink" onClick={() => setDrawerOpen(false)}>
                <i className="bi bi-house" />{t("home") || "Home"}
              </NavLink>

              {navCats.map((cat) => {
                const hasKids  = Array.isArray(cat.children) && cat.children.length > 0;
                const expanded = !!expandedCats[cat.id];
                return (
                  <div key={cat.id}>
                    <div className="nh-dlink-row">
                      <NavLink
                        to={link(`/category/${cat.slug}`)}
                        className="nh-dlink nh-dlink--flex"
                        onClick={() => setDrawerOpen(false)}
                      >
                        {cat.name}
                      </NavLink>
                      {hasKids && (
                        <button
                          type="button"
                          className={`nh-dexpand${expanded ? " nh-dexpand--open" : ""}`}
                          onClick={() => toggleCatExpand(cat.id)}
                          aria-expanded={expanded}
                        >
                          <i className="bi bi-chevron-down" />
                        </button>
                      )}
                    </div>
                    {hasKids && expanded && (
                      <div className="nh-dchildren">
                        {cat.children.map((ch) => (
                          <NavLink
                            key={ch.id}
                            to={link(`/category/${ch.slug}`)}
                            className="nh-dchild"
                            onClick={() => setDrawerOpen(false)}
                          >
                            <i className="bi bi-arrow-right-short" />{ch.name}
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}

              <NavLink to={link("/subjects")} className="nh-dlink" onClick={() => setDrawerOpen(false)}>
                <i className="bi bi-grid-3x3-gap" />{t("subjects") || "Subjects"}
              </NavLink>
              <NavLink to={link("/contact")} className="nh-dlink" onClick={() => setDrawerOpen(false)}>
                <i className="bi bi-envelope" />{t("contact") || "Contact"}
              </NavLink>
            </nav>
          </div>

          {/* Subjects grid */}
          {subjects.length > 0 && (
            <div className="nh-dsec">
              <div className="nh-dlabel">{t("subjects") || "Subjects"}</div>
              <div className="nh-subject-grid">
                {subjects.map((s) => {
                  const to = s.url || link(`/subject/${s.slug}`);
                  if (s.url) return (
                    <a key={s.id} href={to} className="nh-schip" onClick={() => setDrawerOpen(false)}>
                      {s.icon && <i className={`bi ${s.icon}`} />}{s.name}
                    </a>
                  );
                  return (
                    <NavLink key={s.id} to={to} className="nh-schip" onClick={() => setDrawerOpen(false)}>
                      {s.icon && <i className={`bi ${s.icon}`} />}{s.name}
                    </NavLink>
                  );
                })}
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="nh-dsec nh-dsec--footer">
            {weather.city && (
              <span className="nh-drawer-weather">
                <i className={`bi ${weather.icon}`} />
                {weather.tempC !== null ? `${weather.tempC}°C · ` : ""}{weather.city}
              </span>
            )}
            <div className="nh-socials nh-socials--lg">
              {[
                ["bi-twitter-x",  "https://twitter.com"],
                ["bi-facebook",   "https://facebook.com"],
                ["bi-instagram",  "https://instagram.com"],
                ["bi-youtube",    "https://youtube.com"],
              ].map(([ico, href]) => (
                <a key={ico} href={href} target="_blank" rel="noopener noreferrer" className="nh-social nh-social--lg">
                  <i className={`bi ${ico}`} />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════ STYLES ═══════════ */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800&family=Noto+Sans+Arabic:wght@400;500;600;700&display=swap');

        :root {
          --nh-font:    'Sora','Helvetica Neue',sans-serif;
          --nh-font-ar: 'Noto Sans Arabic','Arabic UI Text',Tahoma,sans-serif;
          --nh-red:     #e63311;
          --nh-red-dk:  #c4280d;
          --nh-ink:     #111;
          --nh-ink2:    #6b7280;
          --nh-bg:      #fff;
          --nh-bg2:     #f7f7f7;
          --nh-border:  rgba(0,0,0,.08);
          --nh-sh-sm:   0 1px 4px rgba(0,0,0,.06);
          --nh-sh-md:   0 8px 24px rgba(0,0,0,.10);
          --nh-sh-lg:   0 24px 60px rgba(0,0,0,.16);
          --nh-r:       10px;
          --nh-t:       0.18s cubic-bezier(.4,0,.2,1);
          --nh-dw:      min(360px, 88vw);
        }

        .nh *, .nh-drawer *, .nh-overlay { box-sizing: border-box; }
        .nh { font-family: var(--nh-font); background: var(--nh-bg); color: var(--nh-ink); }
        .nh--rtl { direction: rtl; }

        /* Container */
        .nh-container {
          width: 100%; max-width: 1320px; margin: 0 auto;
          padding-left:  clamp(12px, 4vw, 32px);
          padding-right: clamp(12px, 4vw, 32px);
        }

        /* ══ TOP STRIP ══ */
        .nh-strip { background: #111; color: #fff; height: 36px; overflow: hidden; }
        .nh-strip-inner { height: 100%; display: flex; align-items: center; gap: 6px; }

        .nh-ticker-wrap { display: flex; align-items: center; gap: 6px; flex: 1; min-width: 0; overflow: hidden; }
        .nh-badge {
          display: inline-flex; align-items: center; gap: 4px;
          background: var(--nh-red); color: #fff;
          font-size: 0.6rem; font-weight: 700; letter-spacing: .06em; text-transform: uppercase;
          padding: 4px 7px; border-radius: 3px; white-space: nowrap; flex-shrink: 0; line-height: 1;
        }
        .nh-badge-txt { display: none; }
        @media (min-width: 420px) { .nh-badge-txt { display: inline; } }
        .nh-ticker-track { flex: 1; min-width: 0; overflow: hidden; }
        .nh-ticker-text { display: block; color: #d1d5db; font-size: 0.8125rem; font-weight: 500; text-decoration: none; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; animation: nh-tin .28s ease; transition: color var(--nh-t); }
        .nh-ticker-text:hover { color: #fff; }
        @keyframes nh-tin { from { opacity:0; transform:translateY(3px); } to { opacity:1; transform:none; } }
        .nh-ticker-btns { display: flex; gap: 2px; flex-shrink: 0; }
        .nh-tick-btn { background: rgba(255,255,255,.1); border: none; color: #9ca3af; width: 22px; height: 22px; border-radius: 4px; cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 0.5rem; transition: background var(--nh-t), color var(--nh-t); }
        .nh-tick-btn:hover { background: rgba(255,255,255,.22); color: #fff; }

        /* ★ FIX: strip meta — hidden on tiny screens, visible from 640px */
        .nh-strip-meta { display: none; align-items: center; gap: 14px; flex-shrink: 0; color: #9ca3af; font-size: 0.73rem; }
        @media (min-width: 640px) { .nh-strip-meta { display: flex; } }

        .nh-date { white-space: nowrap; }

        /* ★ FIX: weather — always show city, truncate if very long */
        .nh-weather {
          display: flex; align-items: center; gap: 5px; white-space: nowrap;
          overflow: hidden; max-width: 200px;
        }
        .nh-weather-city {
          /* ★ always visible — removed the media-query that hid it */
          color: #9ca3af;
          overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
          max-width: 120px;
        }

        .nh-socials { display: flex; gap: 10px; }
        .nh-socials--lg { gap: 14px; }
        .nh-social { color: #6b7280; font-size: 0.875rem; text-decoration: none; transition: color var(--nh-t); }
        .nh-social:hover { color: #fff; }
        .nh-social--lg { font-size: 1.125rem; }

        /* ══ MASTHEAD ══ */
        .nh-masthead { background: var(--nh-bg); border-bottom: 1px solid var(--nh-border); }
        .nh-masthead-inner { height: 60px; display: flex; align-items: center; gap: 10px; }
        @media (min-width: 640px) { .nh-masthead-inner { height: 68px; gap: 14px; } }

        /* Logo — NEVER shrinks */
        .nh-brand { display: flex; align-items: center; gap: 8px; text-decoration: none; color: var(--nh-ink); flex-shrink: 0; }
        .nh-brand--sm { gap: 6px; }
        .nh-logo-svg { display: block; flex-shrink: 0; }
        .nh-brand-text { display: flex; flex-direction: column; line-height: 1; }
        .nh-brand-name { font-size: 1.125rem; font-weight: 800; letter-spacing: -.03em; white-space: nowrap; line-height: 1; }
        @media (min-width: 480px) { .nh-brand-name { font-size: 1.3rem; } }
        .nh-brand-num { color: var(--nh-red); }
        .nh-brand-sub { font-size: 0.6rem; color: var(--nh-ink2); font-weight: 500; margin-top: 2px; display: none; }
        @media (min-width: 640px) { .nh-brand-sub { display: block; } }

        /* Desktop search */
        .hs-wrap--desktop { flex: 1; max-width: 420px; position: relative; display: none; }
        @media (min-width: 1024px) { .hs-wrap--desktop { display: block; } }

        /* Actions cluster */
        .nh-actions { display: flex; align-items: center; gap: 7px; margin-left: auto; flex-shrink: 0; }
        .nh--rtl .nh-actions { margin-left: 0; margin-right: auto; }

        /* Language pills — desktop */
        .nh-lang-grp { display: none; align-items: center; background: var(--nh-bg2); border: 1px solid var(--nh-border); border-radius: 8px; padding: 3px; gap: 2px; }
        @media (min-width: 768px) { .nh-lang-grp { display: flex; } }
        .nh-lang-btn { background: none; border: none; padding: 5px 9px; border-radius: 6px; font-size: 0.7rem; font-weight: 700; color: var(--nh-ink2); cursor: pointer; font-family: var(--nh-font); transition: background var(--nh-t), color var(--nh-t); white-space: nowrap; }
        .nh-lang-btn:hover { background: rgba(0,0,0,.05); color: var(--nh-ink); }
        .nh-lang-btn--on { background: #e0370d; color: #fff; box-shadow: 0 1px 4px rgba(0,0,0,.12); }

        /* Auth btns — desktop */
        .nh-auth-btns { display: none; align-items: center; gap: 7px; }
        @media (min-width: 768px) { .nh-auth-btns { display: flex; } }
        .nh-btn-ghost { display: inline-flex; align-items: center; justify-content: center; padding: 7px 14px; border: 1.5px solid var(--nh-border); border-radius: 8px; color: var(--nh-ink); font-size: 0.79rem; font-weight: 600; text-decoration: none; background: none; cursor: pointer; font-family: var(--nh-font); white-space: nowrap; transition: border-color var(--nh-t), background var(--nh-t); }
        .nh-btn-ghost:hover { border-color: var(--nh-ink); background: var(--nh-bg2); color: var(--nh-ink); }
        .nh-btn--full { width: 100%; }
        .nh-btn-solid { display: inline-flex; align-items: center; justify-content: center; padding: 7px 14px; background: var(--nh-red); border: none; border-radius: 8px; color: #fff; font-size: 0.79rem; font-weight: 700; text-decoration: none; cursor: pointer; font-family: var(--nh-font); white-space: nowrap; transition: background var(--nh-t), transform .1s; }
        .nh-btn-solid:hover  { background: var(--nh-red-dk); color: #fff; }
        .nh-btn-solid:active { transform: scale(.97); }

        /* Avatar button — desktop */
        .nh-user-dd { display: none; }
        @media (min-width: 768px) { .nh-user-dd { display: block; } }
        .nh-avatar-btn { display: flex; align-items: center; gap: 7px; background: none; border: 1.5px solid var(--nh-border); border-radius: 10px; padding: 5px 10px 5px 5px; cursor: pointer; font-family: var(--nh-font); font-size: 0.79rem; font-weight: 600; color: var(--nh-ink); transition: border-color var(--nh-t); }
        .nh-avatar-btn:hover { border-color: var(--nh-ink); }
        .nh-avatar { width: 30px; height: 30px; border-radius: 50%; background: linear-gradient(135deg, var(--nh-red), #ff6b35); color: #fff; font-weight: 800; font-size: 0.6875rem; display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .nh-avatar--lg { width: 38px; height: 38px; font-size: 0.8rem; }
        .nh-avatar-name { max-width: 100px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .nh-avatar-chev { font-size: 0.5rem; color: var(--nh-ink2); }
        .nh-user-menu { min-width: 200px; padding: 4px; border-radius: var(--nh-r) !important; box-shadow: var(--nh-sh-lg) !important; border: 1px solid var(--nh-border) !important; }
        .nh-user-header { display: flex; align-items: center; gap: 10px; padding: 8px; }
        .nh-user-name { font-weight: 700; font-size: 0.875rem; color: #ffffff; }
        .nh-dditem { display: flex !important; align-items: center; gap: 8px; border-radius: 7px; font-size: 0.8rem !important; font-weight: 500; padding: 8px 10px !important; color: var(--nh-ink) !important; transition: background var(--nh-t) !important; }
        .nh-dditem:hover { background: var(--nh-bg2) !important; }
        .nh-dditem--danger { color: #dc2626 !important; }
        .nh-dditem--danger:hover { background: #fef2f2 !important; }
        .nh-confirm-row { display: flex; align-items: center; gap: 6px; padding: 8px 10px; font-size: 0.79rem; }
        .nh-confirm-q { font-weight: 700; color: #dc2626; flex: 1; }
        .nh-confirm-yes { background: #dc2626; color: #fff; border: none; border-radius: 6px; padding: 3px 10px; font-size: 0.73rem; font-weight: 700; cursor: pointer; }
        .nh-confirm-no  { background: var(--nh-bg2); color: var(--nh-ink); border: 1px solid var(--nh-border); border-radius: 6px; padding: 3px 10px; font-size: 0.73rem; font-weight: 600; cursor: pointer; }

        /* Icon button */
        .nh-icon-btn { width: 36px; height: 36px; background: var(--nh-bg2); border: 1px solid var(--nh-border); border-radius: 9px; cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 0.875rem; color: var(--nh-ink2); transition: background var(--nh-t), color var(--nh-t); flex-shrink: 0; }
        .nh-icon-btn:hover { background: #efefef; color: var(--nh-ink); }
        .nh-logout-btn:hover { background: #fef2f2 !important; color: #dc2626 !important; border-color: #fca5a5 !important; }
        .nh-search-toggle { display: flex; }
        @media (min-width: 1024px) { .nh-search-toggle { display: none; } }

        /* Hamburger */
        .nh-hamburger { display: flex; flex-direction: column; justify-content: center; gap: 5px; width: 36px; height: 36px; background: var(--nh-bg2); border: 1px solid var(--nh-border); border-radius: 9px; padding: 0 8px; cursor: pointer; flex-shrink: 0; }
        @media (min-width: 1024px) { .nh-hamburger { display: none; } }
        .nh-hamburger span { display: block; height: 1.5px; background: var(--nh-ink); border-radius: 2px; transform-origin: center; transition: transform var(--nh-t), opacity var(--nh-t); }
        .nh-hamburger--open span:nth-child(1) { transform: translateY(6.5px) rotate(45deg); }
        .nh-hamburger--open span:nth-child(2) { opacity: 0; }
        .nh-hamburger--open span:nth-child(3) { transform: translateY(-6.5px) rotate(-45deg); }

        /* ══ MOBILE SEARCH ══ */
        .nh-mobile-search { max-height: 0; overflow: hidden; background: var(--nh-bg); border-top: 1px solid transparent; transition: max-height .28s ease, border-color .28s ease, padding .28s ease; }
        .nh-mobile-search--open { max-height: 130px; border-top-color: var(--nh-border); padding: 10px 0 12px; }
        @media (min-width: 1024px) { .nh-mobile-search { display: none; } }

        /* ══ STICKY DESKTOP NAV ══ */
        .nh-nav { background: var(--nh-bg); border-bottom: 1px solid var(--nh-border); box-shadow: var(--nh-sh-sm); position: sticky; top: 0; z-index: 990; height: 46px; display: none; }
        @media (min-width: 1024px) { .nh-nav { display: block; } }
        .nh-nav-inner { height: 100%; display: flex; align-items: center; justify-content: space-between; }
        .nh-nav-list { list-style: none; margin: 0; padding: 0; display: flex; align-items: center; gap: 2px; height: 100%; }
        .dnav-item { position: relative; height: 100%; display: flex; align-items: center; }
        .dnav-link { display: flex; align-items: center; gap: 4px; padding: 0 13px; height: 100%; color: var(--nh-ink); text-decoration: none; font-size: 0.79rem; font-weight: 600; white-space: nowrap; position: relative; transition: color var(--nh-t); }
        .dnav-link::after { content:''; position:absolute; bottom:0; left:13px; right:13px; height:2px; background:var(--nh-red); border-radius:2px 2px 0 0; transform:scaleX(0); transform-origin:center; transition:transform var(--nh-t); }
        .dnav-link:hover, .dnav-link.active { color: var(--nh-red); }
        .dnav-link:hover::after, .dnav-link.active::after { transform: scaleX(1); }
        .dnav-chev { font-size: 0.5rem; transition: transform var(--nh-t); }
        .dnav-has-mega:hover .dnav-chev { transform: rotate(180deg); }
        .dnav-mega { min-width: 300px !important; max-width: 580px !important; border-radius: var(--nh-r) !important; box-shadow: var(--nh-sh-lg) !important; border: 1px solid var(--nh-border) !important; }
        .dnav-mega-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 3px; margin-bottom: 10px; }
        .dnav-mega-item { display: block; padding: 7px 10px; border-radius: 7px; font-size: 0.79rem; font-weight: 500; color: var(--nh-ink); text-decoration: none; transition: background var(--nh-t), color var(--nh-t); }
        .dnav-mega-item:hover, .dnav-mega-item.active { background: #fef2f2; color: var(--nh-red); }
        .dnav-mega-footer { border-top: 1px solid var(--nh-border); padding-top: 8px; text-align: right; }
        .nh--rtl .dnav-mega-footer { text-align: left; }
        .dnav-see-all { display: inline-flex; align-items: center; gap: 4px; font-size: 0.73rem; font-weight: 700; color: var(--nh-red); text-decoration: none; }
        .nh-cat-crumb { display: flex; align-items: center; gap: 5px; font-size: 0.72rem; color: var(--nh-ink2); font-weight: 500; max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; padding: 4px 10px; background: var(--nh-bg2); border-radius: 6px; border: 1px solid var(--nh-border); flex-shrink: 0; }

        /* ══ OVERLAY + DRAWER ══ */
        .nh-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.52); backdrop-filter: blur(3px); z-index: 1040; opacity: 0; pointer-events: none; transition: opacity .28s ease; }
        .nh-overlay--on { opacity: 1; pointer-events: all; }
        .nh-drawer { position: fixed; top: 0; bottom: 0; right: -100%; width: var(--nh-dw); background: var(--nh-bg); z-index: 1050; display: flex; flex-direction: column; box-shadow: var(--nh-sh-lg); transition: right .3s cubic-bezier(.4,0,.2,1); overflow: hidden; }
        .nh-drawer--rtl { right: auto; left: -100%; transition: left .3s cubic-bezier(.4,0,.2,1); }
        .nh-drawer--open:not(.nh-drawer--rtl) { right: 0; }
        .nh-drawer--rtl.nh-drawer--open { left: 0; }
        @media (min-width: 1024px) { .nh-drawer, .nh-overlay { display: none !important; } }

        .nh-drawer-head { display: flex; align-items: center; justify-content: space-between; padding: 13px 15px; border-bottom: 1px solid var(--nh-border); flex-shrink: 0; }
        .nh-drawer-close { width: 32px; height: 32px; border-radius: 8px; border: 1px solid var(--nh-border); background: none; cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 0.79rem; color: var(--nh-ink2); transition: background var(--nh-t); }
        .nh-drawer-close:hover { background: var(--nh-bg2); color: var(--nh-ink); }
        .nh-drawer-body { flex: 1; overflow-y: auto; overscroll-behavior: contain; }
        .nh-drawer-body::-webkit-scrollbar { width: 3px; }
        .nh-drawer-body::-webkit-scrollbar-thumb { background: #e5e7eb; border-radius: 3px; }

        .nh-dsec { padding: 13px 15px; border-bottom: 1px solid var(--nh-border); }
        .nh-dsec:last-child { border-bottom: none; }
        .nh-dsec--footer { display: flex; flex-direction: column; gap: 12px; }
        .nh-dlabel { font-size: 0.62rem; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; color: var(--nh-ink2); margin-bottom: 10px; }
        .nh-drawer-user { display: flex; align-items: center; gap: 11px; }
        .nh-drawer-profile-link { font-size: 0.73rem; color: var(--nh-red); font-weight: 600; text-decoration: none; }
        .nh-drawer-auth { display: flex; gap: 8px; }
        .nh-drawer-weather { display: flex; align-items: center; gap: 6px; font-size: 0.79rem; color: var(--nh-ink2); }

        /* Language pills */
        .nh-lang-pills { display: grid; grid-template-columns: repeat(2,1fr); gap: 7px; }
        .nh-lang-pill { display: flex; flex-direction: column; align-items: center; gap: 2px; padding: 10px 8px; border-radius: 9px; border: 1.5px solid var(--nh-border); background: var(--nh-bg2); cursor: pointer; font-family: var(--nh-font); transition: border-color var(--nh-t), background var(--nh-t), box-shadow var(--nh-t); }
        .nh-lang-pill:hover { border-color: var(--nh-ink); background: #fff; }
        .nh-lang-pill--on { border-color: var(--nh-red); background: #fef2f2; box-shadow: 0 0 0 3px rgba(230,51,17,.08); }
        .nh-pill-code { font-size: 1rem; font-weight: 800; color: var(--nh-ink); line-height: 1; }
        .nh-lang-pill--on .nh-pill-code { color: var(--nh-red); }
        .nh-pill-name { font-size: 0.68rem; color: var(--nh-ink2); font-weight: 500; }

        /* Drawer nav links */
        .nh-dlink { display: flex; align-items: center; gap: 9px; padding: 10px 9px; border-radius: 8px; color: var(--nh-ink); text-decoration: none; font-size: 0.875rem; font-weight: 600; transition: background var(--nh-t), color var(--nh-t); }
        .nh-dlink:hover, .nh-dlink.active { background: #fef2f2; color: var(--nh-red); }
        .nh-dlink--flex { flex: 1; }
        .nh-dlink-row { display: flex; align-items: center; border-radius: 8px; }
        .nh-dexpand { width: 36px; height: 40px; display: flex; align-items: center; justify-content: center; background: none; border: none; cursor: pointer; color: var(--nh-ink2); font-size: 0.65rem; border-radius: 8px; flex-shrink: 0; transition: transform var(--nh-t), background var(--nh-t); }
        .nh-dexpand:hover { background: var(--nh-bg2); color: var(--nh-ink); }
        .nh-dexpand--open { transform: rotate(180deg); }
        .nh-dchildren { padding-left: 18px; display: flex; flex-direction: column; gap: 1px; animation: nh-exp .16s ease; }
        .nh--rtl .nh-dchildren { padding-left: 0; padding-right: 18px; }
        @keyframes nh-exp { from { opacity:0; transform:translateY(-4px); } to { opacity:1; transform:none; } }
        .nh-dchild { display: flex; align-items: center; gap: 3px; padding: 7px 9px; border-radius: 7px; font-size: 0.8rem; font-weight: 500; color: var(--nh-ink2); text-decoration: none; transition: background var(--nh-t), color var(--nh-t); }
        .nh-dchild:hover, .nh-dchild.active { background: #fef2f2; color: var(--nh-red); }

        /* Subject grid */
        .nh-subject-grid { display: grid; grid-template-columns: repeat(2,1fr); gap: 6px; }
        @media (min-width: 420px) { .nh-subject-grid { grid-template-columns: repeat(3,1fr); } }
        .nh-schip { display: flex; align-items: center; gap: 5px; padding: 7px 9px; border-radius: 7px; border: 1px solid var(--nh-border); background: var(--nh-bg2); font-size: 0.73rem; font-weight: 600; color: var(--nh-ink); text-decoration: none; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; transition: border-color var(--nh-t), background var(--nh-t), color var(--nh-t); }
        .nh-schip:hover { border-color: var(--nh-red); background: #fef2f2; color: var(--nh-red); }

        /* ══ SEARCH SYSTEM ══ */
        .hs-wrap { position: relative; }
        .hs-bar { display: flex; align-items: center; gap: 6px; background: var(--nh-bg2); border: 1.5px solid var(--nh-border); border-radius: 10px; padding: 7px 7px 7px 11px; direction: ltr; transition: border-color var(--nh-t), background var(--nh-t), box-shadow var(--nh-t); }
        .hs-bar--rtl { direction: rtl; padding: 7px 11px 7px 7px; }
        .hs-bar:focus-within { background: #fff; border-color: var(--nh-red); box-shadow: 0 0 0 3px rgba(230,51,17,.1); }
        .hs-icon { color: #bbb; font-size: 0.875rem; flex-shrink: 0; transition: color var(--nh-t); }
        .hs-bar:focus-within .hs-icon { color: var(--nh-red); }
        .hs-input { flex: 1; min-width: 0; border: none; background: transparent; font-size: 0.875rem; color: var(--nh-ink); font-family: var(--nh-font); outline: none; padding: 1px 0; }
        .hs-input[lang="ar"] { font-family: var(--nh-font-ar); font-size: 0.9375rem; line-height: 1.6; }
        .hs-input::placeholder { color: #bbb; }
        .hs-input::-webkit-search-cancel-button { display: none; }
        .hs-spinner { width: 14px; height: 14px; flex-shrink: 0; border: 2px solid #e5e5e5; border-top-color: var(--nh-red); border-radius: 50%; animation: hs-spin .65s linear infinite; }
        @keyframes hs-spin { to { transform: rotate(360deg); } }
        .hs-btn-icon { background: none; border: none; color: #bbb; cursor: pointer; display: flex; align-items: center; justify-content: center; width: 26px; height: 26px; border-radius: 6px; font-size: 0.73rem; flex-shrink: 0; transition: background var(--nh-t), color var(--nh-t); }
        .hs-btn-icon:hover { background: rgba(0,0,0,.06); color: var(--nh-ink); }
        .hs-btn-go { background: var(--nh-red); border: none; color: #fff; width: 30px; height: 30px; border-radius: 7px; display: flex; align-items: center; justify-content: center; font-size: 0.8rem; flex-shrink: 0; cursor: pointer; padding: 0; transition: background var(--nh-t), transform .1s; }
        .hs-btn-go:hover  { background: var(--nh-red-dk); }
        .hs-btn-go:active { transform: scale(.93); }

        /* Dropdown */
        .hs-dropdown { position: absolute; top: calc(100% + 6px); left: 0; right: 0; background: #fff; border: 1px solid var(--nh-border); border-radius: 12px; box-shadow: var(--nh-sh-lg); z-index: 3000; overflow: hidden; max-height: 420px; overflow-y: auto; animation: hs-ap .14s ease; }
        @media (max-width: 575px) { .hs-dropdown { position: fixed; top: auto; bottom: 0; left: 0; right: 0; max-height: 65vh; border-radius: 16px 16px 0 0; } }
        .hs-dropdown--rtl { font-family: var(--nh-font-ar); direction: rtl; text-align: right; }
        @keyframes hs-ap { from { opacity:0; transform:translateY(-5px); } to { opacity:1; transform:none; } }
        .hs-dropdown::-webkit-scrollbar { width: 3px; }
        .hs-dropdown::-webkit-scrollbar-thumb { background: #e5e7eb; border-radius: 3px; }
        .hs-result { display: flex; align-items: center; gap: 9px; width: 100%; padding: 9px 12px; background: none; border: none; border-bottom: 1px solid rgba(0,0,0,.04); cursor: pointer; text-align: start; transition: background .08s; min-height: 50px; }
        .hs-result--rtl { flex-direction: row-reverse; }
        .hs-result:last-of-type { border-bottom: none; }
        .hs-result:hover, .hs-result--focused { background: #fef2f2; }
        .hs-thumb { width: 50px; height: 36px; border-radius: 6px; overflow: hidden; flex-shrink: 0; background: #f0f0f0; }
        .hs-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
        .hs-thumb-fallback { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; color: #ccc; font-size: 1rem; }
        .hs-body { flex: 1; min-width: 0; }
        .hs-cat { display: inline-block; font-size: 0.6rem; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; color: var(--nh-red); margin-bottom: 2px; }
        .hs-dropdown--rtl .hs-cat { letter-spacing: 0; }
        .hs-title { font-size: 0.8125rem; font-weight: 600; color: var(--nh-ink); line-height: 1.4; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
        .hs-dropdown--rtl .hs-title { font-family: var(--nh-font-ar); font-size: 0.875rem; line-height: 1.6; font-weight: 700; }
        .hs-excerpt { font-size: 0.7rem; color: #888; margin-top: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .hs-arrow { color: #d1d5db; font-size: 0.73rem; flex-shrink: 0; transition: transform var(--nh-t), color var(--nh-t); }
        .hs-result:hover .hs-arrow, .hs-result--focused .hs-arrow { color: var(--nh-red); }
        .hs-result:not(.hs-result--rtl):hover .hs-arrow, .hs-result:not(.hs-result--rtl).hs-result--focused .hs-arrow { transform: translateX(3px); }
        .hs-result--rtl:hover .hs-arrow, .hs-result--rtl.hs-result--focused .hs-arrow { transform: translateX(-3px); }
        .hs-empty { display: flex; align-items: center; gap: 8px; padding: 20px 14px; color: #bbb; font-size: 0.8125rem; }
        .hs-empty-icon { font-size: 1rem; }
        .hs-footer { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 8px; padding: 8px 12px; font-size: 0.68rem; color: #9ca3af; background: var(--nh-bg2); border-top: 1px solid var(--nh-border); position: sticky; bottom: 0; }
        .hs-footer-label em { font-style: normal; color: var(--nh-ink); font-weight: 600; }
        .hs-kbd { background: #f3f4f6; border: 1px solid #d1d5db; border-radius: 4px; padding: 1px 6px; font-size: 0.6rem; color: #9ca3af; font-family: monospace; }

        /* ── RTL ── */
        .nh--rtl .nh-brand-name,
        .nh--rtl .dnav-link,
        .nh--rtl .nh-dlink,
        .nh--rtl .nh-dchild,
        .nh--rtl .nh-schip,
        .nh--rtl .nh-lang-btn,
        .nh--rtl .nh-btn-ghost,
        .nh--rtl .nh-btn-solid { font-family: var(--nh-font-ar); }

        /* ── Dark mode ── */
        @media (prefers-color-scheme: dark) {
          :root {
            --nh-ink: #f1f1f1; --nh-ink2: #9ca3af;
            --nh-bg: #111;    --nh-bg2:  #1a1a1a;
            --nh-border: rgba(255,255,255,.09);
          }
          .nh-strip { background: #0a0a0a; }
          .hs-bar { background: #1a1a1a; }
          .hs-bar:focus-within { background: #111; }
          .hs-input { color: #f1f1f1; }
          .hs-dropdown { background: #161616; border-color: rgba(255,255,255,.1); }
          .hs-result:hover, .hs-result--focused { background: #1e0f0f; }
          .hs-footer { background: #0e0e0e; }
          .nh-drawer { background: #111; }
          .nh-lang-pill { background: #1a1a1a; }
          .nh-lang-pill:hover { background: #222; }
          .nh-lang-pill--on { background: #1e0f0f; }
          .nh-dlink:hover, .nh-dlink.active { background: #1e0f0f; }
          .nh-dchild:hover, .nh-dchild.active { background: #1e0f0f; }
          .nh-schip { background: #1a1a1a; }
          .nh-schip:hover { background: #1e0f0f; }
          .dnav-mega-item:hover, .dnav-mega-item.active { background: #1e0f0f; }
          .dropdown-menu { background: #161616 !important; border-color: rgba(255,255,255,.1) !important; }
          .dropdown-item, .nh-dditem { color: #f1f1f1 !important; }
          .nh-dditem:hover { background: #222 !important; }
          .nh-dditem--danger { color: #f87171 !important; }
          .nh-dditem--danger:hover { background: #2a1111 !important; }
          .nh-mobile-search { background: #111; }
          .hs-kbd { background: #222; border-color: #333; color: #6b7280; }
          .nh-weather-city { color: #6b7280; }
        }
      `}</style>
    </>
  );
}
